import React, { useEffect, useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import {
    Box,
    Typography,
    Button,
    CircularProgress,
    Alert,
} from "@mui/material";
import { toast } from "react-toastify";
import useAuthHeader from "react-auth-kit/hooks/useAuthHeader";
import AddInvoice from "../AddSale/index";

const EditInvoice = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const authHeader = useAuthHeader();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [originalInvoice, setOriginalInvoice] = useState(null);

    useEffect(() => {
        const fetchInvoice = async () => {
            try {
                const response = await fetch(
                    `${process.env.REACT_APP_BACKEND_URL}/invoices/${id}`,
                    {
                        method: "GET",
                        headers: {
                            "Content-Type": "application/json",
                            Authorization: authHeader,
                        },
                    }
                );

                if (response.ok) {
                    const data = await response.json();
                    setOriginalInvoice(data.data);
                } else {
                    throw new Error("Failed to fetch invoice");
                }
            } catch (err) {
                setError(err.message);
                toast.error("Failed to load invoice");
            } finally {
                setLoading(false);
            }
        };

        fetchInvoice();
    }, [id, authHeader]);

    const handleEditSubmit = async (newInvoiceData) => {
        try {
            // Step 1: Delete the old invoice
            const deleteResponse = await fetch(
                `${process.env.REACT_APP_BACKEND_URL}/invoices/${id}`,
                {
                    method: "DELETE",
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: authHeader,
                    },
                }
            );

            if (!deleteResponse.ok) {
                throw new Error("Failed to delete original invoice");
            }

            // Step 2: Create new invoice with the edited data
            const createResponse = await fetch(
                `${process.env.REACT_APP_BACKEND_URL}/invoices/create`,
                {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: authHeader,
                    },
                    body: JSON.stringify(newInvoiceData),
                }
            );

            if (!createResponse.ok) {
                throw new Error("Failed to create new invoice");
            }

            const result = await createResponse.json();
            toast.success("Invoice updated successfully!");
            navigate(`/Invoice/${result.invoice.id}`);
        } catch (err) {
            toast.error(`Error updating invoice: ${err.message}`);
            console.error("Edit error:", err);
        }
    };

    if (loading) {
        return (
            <Box
                sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    height: "100vh",
                }}
            >
                <CircularProgress />
            </Box>
        );
    }

    if (error) {
        return (
            <Box sx={{ p: 3 }}>
                <Alert severity="error">{error}</Alert>
                <Button
                    variant="contained"
                    onClick={() => navigate("/SalesList")}
                    sx={{ mt: 2 }}
                >
                    Back to Sales List
                </Button>
            </Box>
        );
    }

    if (!originalInvoice) {
        return (
            <Box sx={{ p: 3 }}>
                <Alert severity="warning">Invoice not found</Alert>
                <Button
                    variant="contained"
                    onClick={() => navigate("/SalesList")}
                    sx={{ mt: 2 }}
                >
                    Back to Sales List
                </Button>
            </Box>
        );
    }

    // Transform invoice data to match AddSalePage format
    const initialData = {
        otherPartyId: originalInvoice.OtherPartyId,
        taxPercentage: Number(originalInvoice.taxPercentage),
        dollarToLiraRate: originalInvoice.dollarToLiraRate
            ? Number(originalInvoice.dollarToLiraRate)
            : null,
        note: originalInvoice.note || "",
        typeId: originalInvoice.typeId,
        serialId: originalInvoice.serialId, // Preserve original serialId
        items:
            originalInvoice.InvoiceItem?.map((invoiceItem, idx) => ({
                id: invoiceItem.itemId,
                name: invoiceItem.item?.name,
                barcode: invoiceItem.item?.barcode,
                quantity: invoiceItem.quantity,
                price: Number(invoiceItem.price),
                discountAmount: Number(invoiceItem.discountAmount),
                discountPercentage: Number(invoiceItem.discountPercentage),
                isTaxable: invoiceItem.isTaxable,
                // add unique lineId so edits affect only this line
                lineId: `${invoiceItem.itemId}-${Date.now()}-${idx}`,
            })) || [],
    };

    return (
        <Box>
            <Box sx={{ mb: 3, display: "flex", alignItems: "center", gap: 2 }}>
                <Typography
                    variant="h4"
                    color="primary"
                    sx={{ fontWeight: "bold" }}
                >
                    Edit Invoice #{id}
                </Typography>
                <Button
                    variant="outlined"
                    onClick={() => navigate("/SalesList")}
                >
                    Cancel
                </Button>
            </Box>
            {/* AddInvoice component expects URL search params, so we navigate with them */}
            {/* For now, render a wrapper that sets up the correct query params */}
            <EditInvoiceWrapper
                invoiceTypeId={originalInvoice.typeId}
                customerId={originalInvoice.OtherPartyId}
                initialData={initialData}
            />
        </Box>
    );
};

// Wrapper component to handle URL params for AddInvoice
const EditInvoiceWrapper = ({ invoiceTypeId, customerId, initialData }) => {
    const navigate = useNavigate();
    const { id } = useParams();

    useEffect(() => {
        // Set the URL params that AddInvoice expects
        const params = new URLSearchParams({
            invoiceType: invoiceTypeId.toString(),
            selectedCustomer: customerId.toString(),
        });
        navigate(`/EditInvoice/${id}?${params.toString()}`, { replace: true });
    }, [invoiceTypeId, customerId, id, navigate]);

    return (
        <AddInvoice
            isEditMode={true}
            initialInvoiceData={initialData}
            editInvoiceId={id}
        />
    );
};

export default EditInvoice;
